import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import type { LegalDocuments, NdaSignaturePayload, TermsConsentPayload, User } from "@/types";
import { getRecaptchaToken } from "@/lib/recaptcha";
import { orgSlugFromPath, rememberOrgDoor } from "@/lib/orgDoor";
import type { Role } from "@/lib/roles";
import { setTheme } from "@/lib/theme";
import { resetModuleVisibility } from "@/lib/moduleVisibility";
import { AUTH_TOKEN_SHARED_KEY, clearAuthToken, getAuthToken, setAuthToken } from "@/lib/authToken";
import { reportBrowserTimezone } from "@/lib/userLocation";

/** Built-in image-captcha answer, sent alongside (or instead of) a reCAPTCHA
    token — see frontend/src/components/Captcha.tsx and app/services/captcha.py. */
export type CaptchaFields = { captcha_token?: string; captcha_answer?: string };

/**
 * Thrown when the credentials are correct but presented at another
 * organization's door. Carries where the user should actually sign in, so the
 * page can offer a link rather than a dead end.
 */
export class WrongOrgDoorError extends Error {
  orgName: string;
  signInUrl: string;
  constructor(message: string, orgName: string, signInUrl: string) {
    super(message);
    this.name = "WrongOrgDoorError";
    this.orgName = orgName;
    this.signInUrl = signInUrl;
  }
}

/** Turn a failed auth response into the richest error we can. */
async function authError(res: Response, fallback: string): Promise<Error> {
  const body = await res.json().catch(() => ({}));
  const detail = body?.detail;
  if (detail && typeof detail === "object" && detail.org_sign_in_url) {
    return new WrongOrgDoorError(detail.message ?? fallback, detail.org_name ?? "", detail.org_sign_in_url);
  }
  return new Error(typeof detail === "string" ? detail : fallback);
}

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string, captcha?: CaptchaFields) => Promise<User>;
  adminLogin: (email: string, password: string, captcha?: CaptchaFields) => Promise<void>;
  register: (data: { email: string; password: string; first_name: string; last_name: string; phone?: string; role?: string; legal_consent: TermsConsentPayload; advisor_code?: string; referral_code?: string }, captcha?: CaptchaFields) => Promise<User>;
  registerBuyer: (data: { email: string; password: string; first_name: string; last_name: string; phone?: string; legal_consent: TermsConsentPayload; tier_id: number; stripe_subscription_id?: string; promo_code?: string; referral_code?: string }, captcha?: CaptchaFields) => Promise<User>;
  socialLogin: (provider: "google" | "facebook", token: string, role?: string, legalConsent?: TermsConsentPayload, advisorCode?: string) => Promise<User | "role_required">;
  getLegalDocuments: () => Promise<LegalDocuments>;
  acceptTerms: (payload: TermsConsentPayload) => Promise<void>;
  signNda: (payload: NdaSignaturePayload) => Promise<User | null>;
  logout: () => void;
  hasRole: (...roles: Role[]) => boolean;
  updateUser: (user: User) => void;
  sendOtp: (email: string, captcha?: CaptchaFields) => Promise<void>;
  verifyOtp: (email: string, otpCode: string) => Promise<void>;
  verifyAdvisorCode: (code: string, captcha?: CaptchaFields) => Promise<void>;
}

const AuthContext = createContext<AuthState>({
  user: null,
  token: null,
  loading: true,
  login: async () => { throw new Error("not implemented"); },
  adminLogin: async () => {},
  register: async () => { throw new Error("not implemented"); },
  registerBuyer: async () => { throw new Error("not implemented"); },
  socialLogin: async () => "role_required" as const,
  getLegalDocuments: async () => ({ terms_text: "", terms_version: "", nda_version: "" }),
  acceptTerms: async () => {},
  signNda: async () => null,
  logout: () => {},
  hasRole: () => false,
  updateUser: () => {},
  sendOtp: async () => {},
  verifyOtp: async () => {},
  verifyAdvisorCode: async () => {},
});

export const useAuth = () => useContext(AuthContext);

const API = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api/v1";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(getAuthToken);
  const [loading, setLoading] = useState(true);

  function storeToken(accessToken: string) {
    setAuthToken(accessToken);
    setToken(accessToken);
    // Every sign-in starts in Light·Gold, overriding whatever the last session
    // left behind — the signed-out surfaces (landing, auth) are pinned dark, the
    // app proper is light. Called from here because storeToken is the one point
    // every successful auth path passes through (password, admin, social, both
    // registrations). Toggling during the session still works and persists until
    // the next sign-in.
    setTheme("light");
    // The module-visibility map is per viewer (it is resolved from their roles)
    // and cached for the life of the tab. Without this, signing in after another
    // account in the same tab reused that account's map — an Operator signing in
    // after a BL advisor lost AI Deal Matching, both calculators and Funding.
    resetModuleVisibility();
  }

  useEffect(() => {
    if (token) {
      fetchProfile();
    } else {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Signing out (or timing out) in one tab removes the shared token; every
  // other open tab on that SAME session must drop it too, or it keeps showing
  // Niva, the notification popups and the signed-in shell until its next failed
  // request. Tabs signed in as a different account are left alone.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== AUTH_TOKEN_SHARED_KEY || e.newValue) return;
      if (e.oldValue && e.oldValue === getAuthToken()) logout();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchProfile(): Promise<User | null> {
    // Read the tab's token at call time, and drop the answer if this tab has
    // signed in as someone else while it was in flight — otherwise a slow
    // buyer /auth/me lands on top of a fresh admin login.
    const sent = getAuthToken();
    const superseded = () => getAuthToken() !== sent;
    try {
      const res = await fetch(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${sent}` },
      });
      if (superseded()) return null;
      if (!res.ok) { logout(); return null; }
      const profile = (await res.json()) as User;
      if (superseded()) return null;
      setUser(profile);
      // Staff see a buyer's local time before calling them (TKT-00157).
      if (profile.roles?.includes("buyer")) reportBrowserTimezone(profile.id);
      return profile;
    } catch {
      if (!superseded()) logout();
      return null;
    } finally {
      setLoading(false);
    }
  }

  async function login(email: string, password: string, captcha?: CaptchaFields) {
    const res = await fetch(`${API}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, org_slug: orgSlugFromPath(), ...captcha, recaptcha_token: await getRecaptchaToken("login") }),
    });
    if (!res.ok) throw await authError(res, "Invalid email or password");
    const data = await res.json();
    storeToken(data.access_token);
    setUser(data.user);
    rememberOrgDoor(orgSlugFromPath());
    if (data.user?.show_expiry_warning) {
      sessionStorage.setItem(
        "login_expiry_warning",
        JSON.stringify({
          expiry_days_remaining: data.user.expiry_days_remaining,
          account_expiry_date: data.user.account_expiry_date,
        }),
      );
    } else {
      sessionStorage.removeItem("login_expiry_warning");
    }
    return data.user as User;
  }

  async function adminLogin(email: string, password: string, captcha?: CaptchaFields) {
    const res = await fetch(`${API}/auth/admin-login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, org_slug: orgSlugFromPath(), ...captcha, recaptcha_token: await getRecaptchaToken("admin_login") }),
    });
    if (!res.ok) throw await authError(res, "Access denied");
    const data = await res.json();
    storeToken(data.access_token);
    setUser(data.user);
    rememberOrgDoor(orgSlugFromPath());
  }

  async function register(data: { email: string; password: string; first_name: string; last_name: string; phone?: string; role?: string; legal_consent: TermsConsentPayload; advisor_code?: string; referral_code?: string }, captcha?: CaptchaFields) {
    const res = await fetch(`${API}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...data, org_slug: orgSlugFromPath(), ...captcha, recaptcha_token: await getRecaptchaToken("register") }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const detail = typeof body.detail === "string" ? body.detail : "Registration failed";
      throw new Error(detail);
    }
    const tokenData = await res.json();
    storeToken(tokenData.access_token);
    setUser(tokenData.user);
    rememberOrgDoor(orgSlugFromPath());
    return tokenData.user as User;
  }

  async function registerBuyer(data: { email: string; password: string; first_name: string; last_name: string; phone?: string; legal_consent: TermsConsentPayload; tier_id: number; stripe_subscription_id?: string; promo_code?: string; referral_code?: string }, captcha?: CaptchaFields) {
    const res = await fetch(`${API}/auth/register/buyer`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...data, org_slug: orgSlugFromPath(), ...captcha, recaptcha_token: await getRecaptchaToken("buyer_register") }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const detail = typeof body.detail === "string" ? body.detail : "Buyer registration failed";
      throw new Error(detail);
    }
    const tokenData = await res.json();
    storeToken(tokenData.access_token);
    setUser(tokenData.user);
    rememberOrgDoor(orgSlugFromPath());
    return tokenData.user as User;
  }

  async function socialLogin(provider: "google" | "facebook", socialToken: string, role?: string, legalConsent?: TermsConsentPayload, advisorCode?: string): Promise<User | "role_required"> {
    const res = await fetch(`${API}/auth/${provider}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: socialToken, role: role || null, legal_consent: legalConsent || null, advisor_code: advisorCode || null }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      if (body.detail === "role_required") return "role_required";
      throw new Error(typeof body.detail === "string" ? body.detail : "Social login failed");
    }
    const data = await res.json();
    storeToken(data.access_token);
    setUser(data.user);
    return data.user as User;
  }

  async function getLegalDocuments(): Promise<LegalDocuments> {
    const res = await fetch(`${API}/auth/legal-documents`);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const detail = typeof body.detail === "string" ? body.detail : "Unable to load legal documents";
      throw new Error(detail);
    }
    return res.json();
  }

  async function acceptTerms(payload: TermsConsentPayload): Promise<void> {
    if (!token) throw new Error("Please login to complete legal acceptance");
    const res = await fetch(`${API}/auth/legal/accept`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const detail = typeof body.detail === "string" ? body.detail : "Unable to accept terms";
      throw new Error(detail);
    }
    await fetchProfile();
  }

  async function signNda(payload: NdaSignaturePayload): Promise<User | null> {
    if (!token) throw new Error("Please login to complete NDA signature");
    const res = await fetch(`${API}/auth/legal/sign-nda`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const detail = typeof body.detail === "string" ? body.detail : "Unable to complete NDA signature";
      throw new Error(detail);
    }
    return fetchProfile();
  }

  async function sendOtp(email: string, captcha?: CaptchaFields) {
    const res = await fetch(`${API}/auth/send-otp`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, ...captcha, recaptcha_token: await getRecaptchaToken("send_otp") }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const detail = typeof body.detail === "string" ? body.detail : "Failed to send OTP";
      throw new Error(detail);
    }
  }

  async function verifyOtp(email: string, otpCode: string) {
    const res = await fetch(`${API}/auth/verify-otp`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, otp_code: otpCode }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const detail = typeof body.detail === "string" ? body.detail : "OTP verification failed";
      throw new Error(detail);
    }
  }

  async function verifyAdvisorCode(code: string, captcha?: CaptchaFields) {
    const res = await fetch(`${API}/auth/verify-advisor-code`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code, ...captcha }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const detail = typeof body.detail === "string" ? body.detail : "Invalid BL Advisor access code";
      throw new Error(detail);
    }
  }

  function logout() {
    clearAuthToken();
    resetModuleVisibility();
    setToken(null);
    setUser(null);
    setLoading(false);
  }

  function hasRole(...roles: Role[]) {
    if (!user) return false;
    return user.roles.some((r) => roles.includes(r));
  }

  function updateUser(updated: User) {
    setUser(updated);
  }

  return (
    <AuthContext.Provider value={{ user, token, loading, login, adminLogin, register, registerBuyer, socialLogin, getLegalDocuments, acceptTerms, signNda, logout, hasRole, updateUser, sendOtp, verifyOtp, verifyAdvisorCode }}>
      {children}
    </AuthContext.Provider>
  );
}
